import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Box,
    Paper,
    Stack,
    Typography,
    Button,
    Chip,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Alert,
    LinearProgress,
    CircularProgress,
    IconButton,
    Tooltip,
} from '@mui/material';
import { toast } from 'react-toastify';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
    WorkerCertification,
    CertReviewStatus,
    CertSourceChannel,
} from '../../models/worker';
import {
    ALLOWED_CERTIFICATION_EXTENSIONS,
    fetchWorkerCertifications,
    uploadCert,
    confirmCert,
    rejectCert,
    deleteCert,
    attachImageToCert,
    getUploadJobStatus,
} from '../../api/workerCertificationsApi';
import socketService from '../../services/socketService';
import CertReviewDialog from '../CertReview/CertReviewDialog';
import AddManualCertModal from './AddManualCertModal';

interface WorkerCertificationsProps {
    workerNumericId: number;
}

const sourceLabel = (sc: CertSourceChannel): string => {
    switch (sc) {
        case CertSourceChannel.WHATSAPP: return 'WhatsApp';
        case CertSourceChannel.WHATSAPP_DECLARATION: return 'Self-declared';
        case CertSourceChannel.DASHBOARD: return 'Dashboard';
        case CertSourceChannel.MANUAL_ENTRY: return 'Manually added';
        default: return 'Other';
    }
};

const today = () => { const d = new Date(); d.setHours(0,0,0,0); return d; };

const expiryBadge = (expiry: string | null) => {
    if (!expiry) return null;
    const d = new Date(expiry);
    const t = today();
    if (d < t) return <Chip size="small" color="error" label={`Expired ${expiry}`} />;
    const sixtyDays = new Date(t); sixtyDays.setDate(sixtyDays.getDate() + 60);
    if (d < sixtyDays) return <Chip size="small" color="warning" label={`Expires ${expiry}`} />;
    return <Chip size="small" color="success" variant="outlined" label={`Valid till ${expiry}`} />;
};

const WorkerCertifications: React.FC<WorkerCertificationsProps> = ({ workerNumericId }) => {
    const [certs, setCerts] = useState<WorkerCertification[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [reviewSelected, setReviewSelected] = useState<WorkerCertification | null>(null);
    const [addManualOpen, setAddManualOpen] = useState(false);
    // v7 cert-ingest pipeline (Step 8). When a fresh upload is in flight we hold
    // the jobId here so we know which `cert_upload_completed` event to act on.
    // Also drives the LinearProgress banner.
    const [uploadingJobId, setUploadingJobId] = useState<string | null>(null);
    const uploadingJobIdRef = useRef<string | null>(null);
    const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const uploadRef = useRef<HTMLInputElement | null>(null);
    const attachRefs = useRef<Record<string, HTMLInputElement | null>>({});

    const refresh = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const rows = await fetchWorkerCertifications(workerNumericId);
            setCerts(rows);
        } catch (e: any) {
            setError(e?.response?.data?.message ?? e?.message ?? 'Failed to load certifications.');
        } finally {
            setLoading(false);
        }
    }, [workerNumericId]);

    useEffect(() => { refresh(); }, [refresh]);

    // ── v7 upload completion path ──────────────────────────────────────────
    // Called by the Socket.IO listener OR the polling fallback when a job's
    // state transitions out of `pending`. Match on jobId so a stray event for
    // a different upload doesn't dismiss our spinner.
    //
    // MUST be declared before the useEffect below that lists it in its deps
    // array — otherwise minified prod builds throw a Temporal Dead Zone error
    // ("Cannot access 'A' before initialization") because Vite's strict-mode
    // evaluator hits the deps reference before reaching this `const`.
    const handleUploadResult = useCallback(
        (result: {
            jobId: string;
            certIds?: string[];
            failedCertIds?: string[];
            reason?: string;
            failed?: boolean;
        }) => {
            if (uploadingJobIdRef.current !== result.jobId) return;

            // Tear down the in-flight state.
            uploadingJobIdRef.current = null;
            setUploadingJobId(null);
            if (pollIntervalRef.current) {
                clearInterval(pollIntervalRef.current);
                pollIntervalRef.current = null;
            }

            if (result.failed) {
                toast.error(result.reason ?? 'Upload failed. Please try again.');
                return;
            }

            const total = result.certIds?.length ?? 0;
            const failed = result.failedCertIds?.length ?? 0;
            const extracted = total - failed;
            if (total === 0) {
                toast.warning("Couldn't extract any certs from this file — please review manually.");
            } else if (failed === 0) {
                toast.success(`Detected ${total} cert(s).`);
            } else if (extracted === 0) {
                toast.warning(`Couldn't extract any of the ${total} cert(s) — please review manually.`);
            } else {
                toast.info(
                    `Detected ${total} cert(s). Extracted ${extracted}; ${failed} couldn't be read — please review manually.`,
                );
            }
            refresh().catch(() => { /* refresh errors are non-fatal */ });
        },
        [refresh],
    );

    // Subscribe to the v7 cert-ingest Socket.IO events for the lifetime of
    // the component. handleUploadResult is jobId-filtered so events for other
    // tabs/workers are no-ops here.
    useEffect(() => {
        const onCompleted = (payload: {
            jobId: string;
            workerId: number;
            certIds: string[];
            failedCertIds: string[];
        }) => {
            handleUploadResult({
                jobId: payload.jobId,
                certIds: payload.certIds,
                failedCertIds: payload.failedCertIds,
            });
        };
        const onFailed = (payload: { jobId: string; reason: string }) => {
            handleUploadResult({ jobId: payload.jobId, failed: true, reason: payload.reason });
        };
        socketService.onCertUploadCompleted(onCompleted);
        socketService.onCertUploadFailed(onFailed);
        return () => {
            socketService.offCertUploadCompleted(onCompleted);
            socketService.offCertUploadFailed(onFailed);
            // Also kill any in-flight poll on unmount.
            if (pollIntervalRef.current) {
                clearInterval(pollIntervalRef.current);
                pollIntervalRef.current = null;
            }
        };
    }, [handleUploadResult]);

    const verified = certs.filter(c => c.status === CertReviewStatus.VERIFIED)
        .sort((a, b) => (a.expiryDate ?? '9999').localeCompare(b.expiryDate ?? '9999'));
    const pending = certs.filter(c => c.status === CertReviewStatus.PENDING_REVIEW || c.status === CertReviewStatus.PENDING_EXTRACTION);
    const unverified = certs.filter(c => c.status === CertReviewStatus.MANUAL_UNVERIFIED);
    const rejected = certs.filter(c => c.status === CertReviewStatus.REJECTED);

    const expiringSoonCount = verified.filter(c => {
        if (!c.expiryDate) return false;
        const d = new Date(c.expiryDate);
        const t = today();
        const sixtyDays = new Date(t); sixtyDays.setDate(sixtyDays.getDate() + 60);
        return d >= t && d < sixtyDays;
    }).length;

    const handleUploadFile = async (file: File) => {
        try {
            const result = await uploadCert(workerNumericId, file);

            // Dedup hit — rows already exist server-side. No spinner, no polling.
            if (result.jobId === null) {
                toast.info(
                    `This file was already uploaded — showing existing ${result.dedupedCertIds.length} cert(s).`,
                );
                await refresh();
                return;
            }

            // New ingest — start the spinner + Socket.IO listening + polling fallback.
            uploadingJobIdRef.current = result.jobId;
            setUploadingJobId(result.jobId);

            // Polling fallback (in case Socket.IO is disconnected). Runs in
            // parallel with the WS listener — whichever fires first dismisses
            // the spinner.
            pollIntervalRef.current = setInterval(async () => {
                const currentJob = uploadingJobIdRef.current;
                if (!currentJob) return;
                try {
                    const status = await getUploadJobStatus(currentJob);
                    if (status.status === 'completed') {
                        handleUploadResult({
                            jobId: currentJob,
                            certIds: status.certIds,
                            failedCertIds: status.failedCertIds,
                        });
                    } else if (status.status === 'failed') {
                        handleUploadResult({
                            jobId: currentJob,
                            failed: true,
                            reason: status.reason,
                        });
                    }
                } catch {
                    // Network blip during polling — keep trying.
                }
            }, 5000);
        } catch (e: any) {
            setError(e?.response?.data?.message ?? e?.message ?? 'Upload failed.');
        }
    };

    const handleAttachImage = async (id: string, file: File) => {
        try {
            await attachImageToCert(id, file);
            await refresh();
        } catch (e: any) {
            setError(e?.response?.data?.message ?? e?.message ?? 'Attach failed.');
        }
    };

    const handleInlineConfirm = async (id: string) => {
        try {
            await confirmCert(id);
            await refresh();
        } catch (e: any) {
            setError(e?.response?.data?.message ?? e?.message ?? 'Confirm failed.');
        }
    };

    const handleInlineReject = async (id: string) => {
        const reason = window.prompt('Reason for rejection:');
        if (!reason?.trim()) return;
        try {
            await rejectCert(id, reason.trim());
            await refresh();
        } catch (e: any) {
            setError(e?.response?.data?.message ?? e?.message ?? 'Reject failed.');
        }
    };

    const handleDelete = async (id: string) => {
        if (!window.confirm('Delete this cert? Verified certs cannot be deleted.')) return;
        try {
            await deleteCert(id);
            await refresh();
        } catch (e: any) {
            setError(e?.response?.data?.message ?? e?.message ?? 'Delete failed.');
        }
    };

    const certRow = (cert: WorkerCertification, includeInlineActions = false) => (
        <Paper key={cert.id} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
            <Stack direction="row" alignItems="center" spacing={2}>
                <Box sx={{ flex: 1, minWidth: 0, cursor: 'pointer' }} onClick={() => setReviewSelected(cert)}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="subtitle2">
                            {cert.proposedCertName || '(unidentified)'}
                        </Typography>
                        <Chip size="small" variant="outlined" label={sourceLabel(cert.sourceChannel)} />
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                        {cert.issuer || '—'} · expires {cert.expiryDate || '?'}
                    </Typography>
                    {cert.workerCorrectionText && (
                        <Typography variant="caption" sx={{ display: 'block', color: 'info.main', mt: 0.5 }}>
                            "Worker says: {cert.workerCorrectionText}"
                        </Typography>
                    )}
                </Box>
                {expiryBadge(cert.expiryDate ?? null)}
                {includeInlineActions && cert.status === CertReviewStatus.PENDING_REVIEW && (
                    <>
                        <Button size="small" onClick={() => setReviewSelected(cert)}>Open review</Button>
                        <Button size="small" color="success" variant="outlined" onClick={() => handleInlineConfirm(cert.id)}>
                            Approve
                        </Button>
                        <Button size="small" color="error" variant="outlined" onClick={() => handleInlineReject(cert.id)}>
                            Reject
                        </Button>
                    </>
                )}
            </Stack>
        </Paper>
    );

    return (
        <Box>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                <Typography variant="h6">Certifications</Typography>
                <Stack direction="row" spacing={1}>
                    <Chip size="small" color="success" icon={<CheckCircleIcon />} label={`${verified.length} verified`} />
                    <Chip size="small" color="warning" icon={<HourglassBottomIcon />} label={`${pending.length} pending`} />
                    <Chip size="small" icon={<WarningAmberIcon />} label={`${unverified.length} unverified`} />
                    {expiringSoonCount > 0 && (
                        <Chip size="small" color="warning" variant="outlined" label={`${expiringSoonCount} expiring soon`} />
                    )}
                </Stack>
                <Box sx={{ flex: 1 }} />
                <Button
                    startIcon={<UploadFileIcon />}
                    variant="outlined"
                    onClick={() => uploadRef.current?.click()}
                    disabled={!!uploadingJobId}
                >
                    Upload file
                </Button>
                <input
                    ref={uploadRef}
                    type="file"
                    hidden
                    accept={ALLOWED_CERTIFICATION_EXTENSIONS.join(',')}
                    onChange={e => {
                        const file = e.target.files?.[0];
                        if (file) handleUploadFile(file);
                        e.target.value = '';
                    }}
                />
                <Button startIcon={<AddIcon />} variant="contained" onClick={() => setAddManualOpen(true)}>
                    Add manually
                </Button>
            </Stack>

            {uploadingJobId && (
                <Box sx={{ mb: 2, p: 2, borderRadius: 1, bgcolor: 'action.hover' }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        Analyzing document — this can take up to 90 seconds for multi-page files…
                    </Typography>
                    <LinearProgress />
                </Box>
            )}

            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

            {loading && <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>}

            {!loading && (
                <Stack spacing={2}>
                    {verified.length > 0 && (
                        <Box>
                            <Typography variant="subtitle1" sx={{ mb: 1 }}>Verified</Typography>
                            {verified.map(c => certRow(c))}
                        </Box>
                    )}

                    {pending.length > 0 && (
                        <Box>
                            <Typography variant="subtitle1" sx={{ mb: 1 }}>Pending review</Typography>
                            {pending.map(c => certRow(c, true))}
                        </Box>
                    )}

                    {unverified.length > 0 && (
                        <Box>
                            <Typography variant="subtitle1" sx={{ mb: 1 }}>Unverified — no source image</Typography>
                            {unverified.map(cert => (
                                <Paper
                                    key={cert.id}
                                    variant="outlined"
                                    sx={{ p: 1.5, mb: 1, bgcolor: 'warning.50', borderColor: 'warning.light' }}
                                >
                                    <Stack direction="row" alignItems="center" spacing={2}>
                                        <WarningAmberIcon color="warning" />
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                <Typography variant="subtitle2">{cert.proposedCertName}</Typography>
                                                <Chip size="small" variant="outlined" label={sourceLabel(cert.sourceChannel)} />
                                            </Stack>
                                            <Typography variant="caption" color="text.secondary">
                                                {cert.issuer || '—'} · No image attached, not approved
                                            </Typography>
                                        </Box>
                                        <Button
                                            size="small"
                                            startIcon={<UploadFileIcon />}
                                            onClick={() => attachRefs.current[cert.id]?.click()}
                                        >
                                            Upload supporting image
                                        </Button>
                                        <input
                                            ref={el => { attachRefs.current[cert.id] = el; }}
                                            type="file"
                                            hidden
                                            accept="image/*,application/pdf,.doc,.docx"
                                            onChange={e => {
                                                const file = e.target.files?.[0];
                                                if (file) handleAttachImage(cert.id, file);
                                                e.target.value = '';
                                            }}
                                        />
                                        <Button size="small" onClick={() => setReviewSelected(cert)}>
                                            Edit
                                        </Button>
                                        <Tooltip title="Delete">
                                            <IconButton size="small" onClick={() => handleDelete(cert.id)}>
                                                <DeleteOutlineIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Stack>
                                </Paper>
                            ))}
                        </Box>
                    )}

                    {rejected.length > 0 && (
                        <Accordion>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="subtitle1">
                                    <CancelIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 1 }} />
                                    Rejected ({rejected.length})
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                {rejected.map(c => certRow(c))}
                            </AccordionDetails>
                        </Accordion>
                    )}

                    {certs.length === 0 && (
                        <Typography color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                            No certifications recorded yet. Upload a file or add one manually to get started.
                        </Typography>
                    )}
                </Stack>
            )}

            <CertReviewDialog
                open={!!reviewSelected}
                cert={reviewSelected}
                onClose={() => setReviewSelected(null)}
                onChanged={() => { refresh(); }}
            />

            <AddManualCertModal
                open={addManualOpen}
                workerId={workerNumericId}
                onClose={() => setAddManualOpen(false)}
                onCreated={refresh}
            />
        </Box>
    );
};

export default WorkerCertifications;
